import manifest from "./photo-manifest.json";

/**
 * Resolve a Shopify listing handle to its ingested photography.
 *
 * The manifest is generated by `script/photo_ingest.mjs` from the harvest
 * library. The app reads THIS, never the harvest tree: those binaries are
 * gitignored and ~412 MB, so a build that depends on them breaks on any fresh
 * checkout and cannot ship.
 *
 * ⚠️ AUTHORIZED, NOT LICENSED. `manifest.authorization` records that Aaron
 * authorized publication on 2026-08-16 OVER the brands' stated terms — four of
 * the six prohibit commercial reuse in writing, two have no grant either way.
 * No brand has said yes. Do not read the presence of a photograph here as
 * permission having been granted.
 */

interface Rendition {
  w: number;
  src: string;
}

interface PhotoEntry {
  srcset: Rendition[];
  /** `contain` for a product, `cover` for a scene. Not interchangeable. */
  fit: "contain" | "cover";
  /** Measured card ground, or null for transparent art and non-flat scenes. */
  ground: number[] | null;
  transparent: boolean;
  portrait: boolean;
  width: number;
  height: number;
  brand: string;
  model: string;
  alt: string;
  /** Optional art direction for full-bleed crops, for example "50% 35%". */
  focal_position?: string;
}

interface BikePhotos {
  studio?: PhotoEntry;
  lifestyle?: PhotoEntry;
  lifestyle_gallery?: PhotoEntry[];
  gallery?: PhotoEntry[];
}

const BIKES = (manifest as { bikes: Record<string, BikePhotos> }).bikes || {};

/** Largest rendition — the `src` fallback for a browser ignoring srcset. */
const largest = (e: PhotoEntry) =>
  [...e.srcset].sort((a, b) => b.w - a.w)[0]?.src;

const srcSetOf = (e: PhotoEntry) =>
  e.srcset.map((r) => `${r.src} ${r.w}w`).join(", ");

const rgb = (c: number[] | null) => (c ? `rgb(${c[0]}, ${c[1]}, ${c[2]})` : null);

const resolvedPhoto = (
  photo: PhotoEntry,
  kind: "studio" | "lifestyle" | "detail",
) => ({
  url: largest(photo),
  srcSet: srcSetOf(photo),
  altText: photo.alt,
  width: photo.width,
  height: photo.height,
  ground: rgb(photo.ground),
  fit: photo.fit,
  focalPosition: photo.focal_position || "center",
  kind,
  source: "manufacturer" as const,
});

/**
 * Everything the card needs for one listing, or an empty object when the
 * listing has no ingested photography.
 *
 * Returning empty rather than throwing is deliberate: a bike with no photo is a
 * normal state — the placeholder branch handles it, and it is what every card
 * showed until this landed.
 */
export function photosFor(handle: string | null | undefined) {
  const entry = handle ? BIKES[handle] : undefined;
  if (!entry) return {};

  const { studio, lifestyle, lifestyle_gallery = [], gallery = [] } = entry;

  const galleryPhotos = [
    ...(lifestyle ? [{ photo: lifestyle, kind: "lifestyle" as const }] : []),
    ...lifestyle_gallery.map((photo) => ({ photo, kind: "lifestyle" as const })),
    ...gallery.map((photo) => ({ photo, kind: "detail" as const })),
  ]
    .map(({ photo, kind }) => resolvedPhoto(photo, kind))
    .filter((photo) => Boolean(photo.url));

  return {
    studio: studio ? resolvedPhoto(studio, "studio") : undefined,
    lifestyleHero: lifestyle ? resolvedPhoto(lifestyle, "lifestyle") : undefined,
    imageUrl: studio ? largest(studio) : undefined,
    imageSrcSet: studio ? srcSetOf(studio) : undefined,
    imageAlt: studio?.alt,
    imageWidth: studio?.width,
    imageHeight: studio?.height,
    imageGround: studio ? rgb(studio.ground) : undefined,

    // Only present when a genuinely different second frame exists. Four of the
    // 44 listings have none, and their cards are correctly static — see the
    // hover rule in HomeSections.BikeCard.
    hoverUrl: lifestyle ? largest(lifestyle) : undefined,
    hoverSrcSet: lifestyle ? srcSetOf(lifestyle) : undefined,

    // Product-page frames only. The studio hero is returned above; this adds
    // the lifestyle image and verified detail shots without making any of
    // those accessory/configuration frames eligible as a catalog hero.
    gallery: galleryPhotos,
  };
}

/** True when the listing has a second frame to reveal. */
export const hasHoverFrame = (handle: string | null | undefined) =>
  Boolean(handle && BIKES[handle]?.lifestyle);

export const photoManifestMeta = manifest as {
  generated: string;
  authorization: Record<string, unknown> | null;
};
